import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PenLine, Printer, ExternalLink, Lock } from "lucide-react";
import { SourcePill, StatusPill, AudiencePill, GovLink, DraftBanner } from "./FormBadges";

// One featured form card (max three shown per functional area)
export default function FormCard({ entry, isManager, labels }) {
  const locked = entry.audience === "manager" && !isManager;
  const usable = entry.usable && !locked;
  return (
    <Card data-testid={`form-card-${entry.id}`} className={`p-4 bg-white border-slate-200 flex flex-col gap-2 ${entry.status === "catalog_only" ? "opacity-90" : "hover-lift"}`}>
      <div className="flex flex-wrap gap-1.5">
        <SourcePill source={entry.source} label={entry.source_label} />
        <AudiencePill audience={entry.audience} label={entry.audience_label} />
      </div>
      <h3 className="font-display font-bold text-[#0A2A5E] leading-snug">{entry.title}</h3>
      <p className="text-xs text-slate-600 flex-1 line-clamp-3">{entry.status === "catalog_only" ? labels.pending : entry.purpose}</p>
      <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
        <StatusPill status={entry.status} label={entry.status_label} />
        {entry.version && <span>{/^\d/.test(entry.version) ? `v${entry.version}` : entry.version}</span>}
        {entry.link_verified && <span>· link verified {entry.link_verified}</span>}
      </div>
      {entry.status === "government" && <div className="text-[11px] text-slate-500">{entry.agency}</div>}
      {entry.status === "beta" && <DraftBanner status="beta" compact />}
      {entry.status === "catalog_only" && <div data-testid="upcoming-label" className="text-[10px] font-bold tracking-wider text-slate-600 border border-dashed border-slate-400 rounded px-2 py-1 text-center">PLANNED — NOT YET AVAILABLE</div>}
      <div className="flex gap-2 pt-1">
        {entry.status === "government" ? (
          <GovLink entry={entry} leavingText={labels.leaving} className="flex-1 inline-flex items-center justify-center h-9 rounded-md bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5A623]">
            Open official form <ExternalLink className="w-4 h-4 ml-2" aria-hidden="true" />
          </GovLink>
        ) : !usable ? (
          <Button disabled className="flex-1 h-9" variant="outline" data-testid={`fill-${entry.id}`} aria-disabled="true">
            <Lock className="w-4 h-4 mr-2" aria-hidden="true" /> {locked ? "HR Managers only" : "Not available for use"}
          </Button>
        ) : (
          <>
            <Link to={`/app/forms/fill/${entry.template_id}`} className="flex-1">
              <Button data-testid={`fill-${entry.id}`} className="w-full h-9 bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white"><PenLine className="w-4 h-4 mr-2" aria-hidden="true" /> Start form</Button>
            </Link>
            <Link to={`/app/forms/print/${entry.template_id}`} aria-label={`Print blank ${entry.title}`}>
              <Button variant="outline" data-testid={`print-${entry.id}`} className="h-9 border-slate-300"><Printer className="w-4 h-4" aria-hidden="true" /></Button>
            </Link>
          </>
        )}
      </div>
    </Card>
  );
}
